import { FC, useMemo, useEffect, useRef, useState } from "react";
import { ReactComponent as Ball } from "../assets/ball.svg";
import { getTeamColors } from "components/colors";
import { Text } from "components/common/Text";

type LastSixPointsProps = {
  statusText: string;
  status: any;
};

export const LastSixPoints: FC<LastSixPointsProps> = ({
  statusText,
  status,
}) => {
  const { home: homeColor, away: awayColor } = getTeamColors(status);

  const points = useMemo(() => {
    const info = statusText?.split("|").find((p) => p.startsWith("INFO="));
    if (!info) return [];
    const raw = info.substring(5);
    return raw
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean)
      .map((t) => {
        const [score, side] = t.split(":");
        return { score, side: side?.trim() as "1" | "2" | undefined };
      })
      .slice(-6);
  }, [statusText]);

  const hasData = points.length === 6;

  const lastNonEmptyPointsRef = useRef<typeof points>([]);
  useEffect(() => {
    if (points.length === 6) lastNonEmptyPointsRef.current = points;
  }, [points]);

  const visibleUntilRef = useRef<number>(0);
  const [forceVisible, setForceVisible] = useState(false);
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!forceVisible) return;
    const id = setInterval(() => {
      const now = Date.now();
      if (now >= visibleUntilRef.current) {
        setForceVisible(false);
        clearInterval(id);
      } else {
        setTick((x) => x + 1);
      }
    }, 500);
    return () => clearInterval(id);
  }, [forceVisible]);

  const displayPoints = hasData ? points : lastNonEmptyPointsRef.current;
  const shouldShow = (hasData || forceVisible) && displayPoints.length === 6;

  if (!shouldShow || !displayPoints.length) return null;

  return (
    <div
      className="mx-auto grid gap-0"
      style={{ width: "clamp(260px, 54vw, 70rem)", marginTop: "10%" }}
    >
      <div className="relative grid grid-cols-6 bg-black/80">
        {displayPoints.map((p, idx) => {
          const accentColor = p.side === "2" ? awayColor : homeColor;
          return (
            <div
              key={`${p?.score}-${idx}`}
              className="relative grid w-[10vw] grid-rows-[auto_auto] place-items-center border-x-[0.12vw] border-white p-[3vw] pb-[1.5vw] first:border-l-0 last:border-r-0"
            >
              <div
                className="absolute left-0 right-0 top-0 h-[1vw]"
                style={{ backgroundColor: accentColor }}
              />
              <div className="h-[4vw] w-[4vw]" style={{ color: accentColor }}>
                <Ball className="h-full w-full fill-current" />
              </div>
              <div className="mt-[1.5vw] font-bold text-white">
                <Text variant="chartValue">{p.score}</Text>
              </div>
            </div>
          );
        })}
      </div>
      <div className="bg-white p-[1vw] text-center font-extrabold tracking-[0.02em] text-black">
        <Text variant="chartTitle">LAST 6 POINTS</Text>
      </div>
    </div>
  );
};
